import FVector from '@data/geometry/FVector';
import { SensorData } from 'Simulations/SensorPoint';
import * as THREE from 'three';
import { Facade } from './Facade';
import { isEdgeBlocked } from './FacadeVisibility';

type Panel = {
    mesh: THREE.Mesh;
    edge: THREE.LineSegments;
};

export type HorizontalEdgePanelInfo = Panel & {
    outerPoint: FVector;
    innerPoint: FVector;
    height: number;
    material: THREE.Material;
};

export class HorizontallyOrientedFacade extends Facade {
    private static readonly COLINEAR_MERGE_DISTANCE_THRESHOLD = 0.01;
    private static readonly EDGE_PANEL_CORNER_THRESHOLD = 0.01;
    private static readonly PANEL_HEIGHT_EPSILON = 1e-4;

    // =====================================
    // STATIC
    // =====================================

    // Removes vertical line between two colinear panels (giving a bigger panel feel):
    static mergeAdjacentPanels(
        targetGroup: THREE.Group,
        currentPanelInfos: HorizontalEdgePanelInfo | undefined,
        nextPanelInfos: HorizontalEdgePanelInfo | undefined
    ): FVector | undefined {
        // Check if merging is necessary:
        if (!currentPanelInfos || !nextPanelInfos) return undefined;
        if (!HorizontallyOrientedFacade.areMergeCompatibleMaterials(currentPanelInfos.material, nextPanelInfos.material)) return undefined;
        if (Math.abs(currentPanelInfos.height - nextPanelInfos.height) > HorizontallyOrientedFacade.PANEL_HEIGHT_EPSILON) return undefined;
        if (!HorizontallyOrientedFacade.areColinear(currentPanelInfos.innerPoint, currentPanelInfos.outerPoint, nextPanelInfos.outerPoint))
            return undefined;
        if (!HorizontallyOrientedFacade.areColinear(currentPanelInfos.innerPoint, currentPanelInfos.outerPoint, nextPanelInfos.innerPoint))
            return undefined;

        // Remove non-merged panels from parent:
        currentPanelInfos.mesh.removeFromParent();
        currentPanelInfos.edge.removeFromParent();
        nextPanelInfos.mesh.removeFromParent();
        nextPanelInfos.edge.removeFromParent();

        const mergedMesh = HorizontallyOrientedFacade.makeVerticalQuad(
            currentPanelInfos.innerPoint,
            nextPanelInfos.innerPoint,
            currentPanelInfos.height,
            currentPanelInfos.material
        );
        const mergedEdge = new THREE.LineSegments(
            new THREE.EdgesGeometry(mergedMesh.geometry, 0.01),
            HorizontallyOrientedFacade.edgeMaterial
        );

        targetGroup.add(mergedMesh, mergedEdge);
        // Return coplanar point:
        return currentPanelInfos.outerPoint.clone();
    }

    // Check for same material to merge two adjacent, coplanar panels into one:
    private static areMergeCompatibleMaterials(left: THREE.Material, right: THREE.Material): boolean {
        if (left === right) return true;
        if (left.type !== right.type) return false;

        const leftWithColor = left as THREE.Material & { color?: THREE.Color; transparent?: boolean; opacity?: number };
        const rightWithColor = right as THREE.Material & { color?: THREE.Color; transparent?: boolean; opacity?: number };
        const leftColor = leftWithColor.color?.getHex();
        const rightColor = rightWithColor.color?.getHex();
        const sameColor = leftColor === rightColor;
        const sameTransparency = !!leftWithColor.transparent === !!rightWithColor.transparent;
        const sameOpacity = Math.abs((leftWithColor.opacity ?? 1) - (rightWithColor.opacity ?? 1)) <= 1e-6;

        return sameColor && sameTransparency && sameOpacity;
    }

    static removeVerticalCoplanarEdges(targetGroup: THREE.Object3D, seamPoint: FVector) {
        targetGroup.updateMatrixWorld(true);
        const seamWorld = targetGroup.localToWorld(new THREE.Vector3(seamPoint.x, seamPoint.y, seamPoint.z));
        const xyTolerance = 1e-3;
        const zTolerance = 1e-6;

        targetGroup.traverse((child) => {
            if (!(child instanceof THREE.LineSegments)) return;

            const position = child.geometry.getAttribute('position');
            if (!position || position.itemSize !== 3 || position.count < 2) return;

            const kept: number[] = [];
            const start = new THREE.Vector3();
            const end = new THREE.Vector3();

            for (let i = 0; i < position.count - 1; i += 2) {
                start.set(position.getX(i), position.getY(i), position.getZ(i));
                end.set(position.getX(i + 1), position.getY(i + 1), position.getZ(i + 1));

                child.localToWorld(start);
                child.localToWorld(end);

                const isVertical =
                    Math.abs(start.x - end.x) <= xyTolerance &&
                    Math.abs(start.y - end.y) <= xyTolerance &&
                    Math.abs(start.z - end.z) > zTolerance;
                const matchesSeam =
                    Math.abs(start.x - seamWorld.x) <= xyTolerance &&
                    Math.abs(start.y - seamWorld.y) <= xyTolerance &&
                    Math.abs(end.x - seamWorld.x) <= xyTolerance &&
                    Math.abs(end.y - seamWorld.y) <= xyTolerance;

                if (isVertical && matchesSeam) continue;

                kept.push(position.getX(i), position.getY(i), position.getZ(i));
                kept.push(position.getX(i + 1), position.getY(i + 1), position.getZ(i + 1));
            }

            if (kept.length === position.array.length) return;
            if (kept.length === 0) {
                child.removeFromParent();
                return;
            }

            const nextGeometry = new THREE.BufferGeometry();
            nextGeometry.setAttribute('position', new THREE.Float32BufferAttribute(kept, 3));
            nextGeometry.computeBoundingSphere();

            child.geometry.dispose();
            child.geometry = nextGeometry;
        });
    }

    private static pointKey(point: FVector): string {
        return `${point.x.toFixed(6)}|${point.y.toFixed(6)}|${point.z.toFixed(6)}`;
    }

    private static distanceXY(a: FVector, b: FVector): number {
        return new THREE.Vector2(a.x - b.x, a.y - b.y).length();
    }

    private static areColinear(
        a: FVector,
        b: FVector,
        c: FVector,
        threshold = HorizontallyOrientedFacade.COLINEAR_MERGE_DISTANCE_THRESHOLD
    ): boolean {
        return HorizontallyOrientedFacade.distancePointToLineXY(c, a, b) <= threshold;
    }

    private static distancePointToLineXY(point: FVector, lineA: FVector, lineB: FVector): number {
        const ab = new THREE.Vector2(lineB.x - lineA.x, lineB.y - lineA.y);
        const ap = new THREE.Vector2(point.x - lineA.x, point.y - lineA.y);
        const abLength = ab.length();
        if (abLength <= 1e-9) return ap.length();

        const twiceTriangleArea = Math.abs(ab.x * ap.y - ab.y * ap.x);
        return twiceTriangleArea / abLength;
    }

    private static makeVerticalQuad(a: FVector, b: FVector, height: number, material: THREE.Material): THREE.Mesh {
        // bottom → top vertices
        const bottom1 = new FVector(a.x, a.y, a.z);
        const bottom2 = new FVector(b.x, b.y, b.z);
        const top1 = new FVector(a.x, a.y, a.z + height);
        const top2 = new FVector(b.x, b.y, b.z + height);

        // Order vertices to form two triangles (quad)
        const basePoints = [top1, bottom1, top2, bottom2];

        const geom = new THREE.BufferGeometry().setFromPoints(basePoints.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
        geom.setIndex([0, 1, 2, 2, 1, 3]);
        geom.computeVertexNormals();

        return new THREE.Mesh(geom, material);
    }

    // =====================================
    // OBJECT
    // =====================================

    private avgDirection = new FVector();
    private perimeter: FVector[];
    private opaqueMaterial: THREE.Material;
    private transparentMaterial: THREE.Material;
    private thickness: number;
    private userSpacing: number;
    private isGroundFloor: boolean;
    private forceOpaque: boolean;

    constructor(
        p1: FVector,
        p2: FVector,
        height: number,
        thickness: number,
        material: THREE.Material,
        opaqueMaterial: THREE.Material,
        transparentMaterial: THREE.Material,
        perimeter: FVector[],
        userSpacing: number,
        isGroundFloor: boolean,
        sensors?: SensorData[],
        direction?: FVector,
        forceOpaque = false
    ) {
        super(p1, p2, height, material, sensors, direction);
        this.thickness = thickness;
        this.opaqueMaterial = opaqueMaterial;
        this.transparentMaterial = transparentMaterial;
        this.perimeter = perimeter;
        this.userSpacing = Math.max(1e-6, userSpacing);
        this.isGroundFloor = isGroundFloor;
        this.forceOpaque = forceOpaque;
    }

    override build(): THREE.Group {
        const group = new THREE.Group();
        this.avgDirection = this.getFacadeDirection()?.clone() ?? new FVector();
        const avgDirXY = new THREE.Vector3(this.avgDirection.x, this.avgDirection.y, 0).normalize();

        // 1. Generate opaque facade, if this facade is not free:
        if (
            this.forceOpaque ||
            isEdgeBlocked(this.p1, this.p2, this.perimeter, this.avgDirection) ||
            (avgDirXY.x === 0 && avgDirXY.y === 0 && avgDirXY.z === 0)
        ) {
            group.add(
                this.buildFacadeQuad(
                    this.p1,
                    this.p2,
                    this.height - this.thickness,
                    this.opaqueMaterial,
                    HorizontallyOrientedFacade.edgeMaterial
                )
            );

            const a = this.p1.clone().add(new FVector(0, 0, this.height - this.thickness));
            const b = this.p2.clone().add(new FVector(0, 0, this.height - this.thickness));
            const edgeMesh = this.makeExtrudedQuad(a, b, this.thickness, this.opaqueMaterial);
            const edge = new THREE.LineSegments(new THREE.EdgesGeometry(edgeMesh.geometry, 0.01), HorizontallyOrientedFacade.edgeMaterial);
            group.add(edge);

            if (this.isGroundFloor) {
                const bottomEdge = edge.clone();
                bottomEdge.position.z -= this.height;
                group.add(bottomEdge);
            }

            return group;
        }

        // 2. Generate transparent facade panels if the facade is free:
        const panels = this.createHorizontalPanels();
        const builtPanels: Panel[] = panels.map((mesh) => ({
            mesh,
            edge: new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry, 0.01), HorizontallyOrientedFacade.edgeMaterial),
        }));
        const edgePanelsByCorner = this.createEdgePanelsByCorner(builtPanels);
        builtPanels.forEach((panel) => {
            group.add(panel.mesh, panel.edge);
        });
        group.userData.edgePanelsByCorner = edgePanelsByCorner;
        group.userData.leadingEdgePanel = edgePanelsByCorner[HorizontallyOrientedFacade.pointKey(this.p1)];
        group.userData.trailingEdgePanel = edgePanelsByCorner[HorizontallyOrientedFacade.pointKey(this.p2)];

        return group;
    }

    // ---------- Core algorithm ----------
    private createHorizontalPanels(): THREE.Mesh[] {
        const meshes: THREE.Mesh[] = [];

        // 1. Get Perpendicular direction to avgDir:
        const avgDir = this.avgDirection.clone().normalize();
        const perpDir = new FVector(-avgDir.y, avgDir.x, 0).normalize();

        // 2. Choose targetEnd & baseStart as the endpoint “closest to avgDir” (interpretation --> larger dot with avgDir):
        const dotP1 = this.p1.dot(avgDir);
        const dotP2 = this.p2.dot(avgDir);
        let baseStart = dotP1 >= dotP2 ? this.p1.clone() : this.p2.clone();
        const targetEnd = baseStart.equals(this.p1) ? this.p2.clone() : this.p1.clone();

        // 3. Point PerpDir towards Target End, if it already isn't:
        const testStep = baseStart.clone().add(perpDir.clone().multiplyScalar(0.1));
        const projectedTest = this.projectOnEdge(testStep, this.p1, this.p2);
        const edgeDir = targetEnd.clone().sub(baseStart).normalize();
        const baseDist = baseStart.dot(edgeDir);
        const testDist = projectedTest.dot(edgeDir);
        const targetDist = targetEnd.dot(edgeDir);
        const movingTowardTarget = Math.abs(testDist - targetDist) < Math.abs(baseDist - targetDist);
        if (!movingTowardTarget) perpDir.multiplyScalar(-1);

        // 4. Measure how far the edge extends in the perpendicular direction (total facade length along perpDir):
        const projectedTargetEndOnPerpDir = this.projectPointOnDirection(targetEnd, baseStart, perpDir);
        const edgePerpendicularToAvgDir = projectedTargetEndOnPerpDir.clone().sub(baseStart);
        const L = edgePerpendicularToAvgDir.mag();
        const numPanels = Math.max(1, Math.floor(L / this.userSpacing));
        const panelWidth = L / numPanels;

        // 5. Generate all the panels of the facade, iteratively:
        for (let i = 0; i < numPanels; i++) {
            // 5.1. Step outward along perpDir by the width of the panel:
            const baseStart1 = baseStart.clone().add(perpDir.clone().multiplyScalar(panelWidth));

            // 5.2. Ensure avgDir points *toward* the facade (i.e., toward the edge line)
            const edgeMid = new FVector((this.p1.x + this.p2.x) / 2, (this.p1.y + this.p2.y) / 2, 0);
            const towardEdge = edgeMid.clone().sub(baseStart1).dot(avgDir) < 0;
            if (!towardEdge) avgDir.multiplyScalar(-1); // flip if pointing outward

            // 5.3. Project baseStart1 toward the edge line along avgDir:
            const projectedBaseStart1 = this.projectPointAlongDirectionOntoEdge(baseStart1, this.p1, this.p2, avgDir);

            // 5.4. Generate the geometry:
            const actualyHeight = this.height - this.thickness;
            meshes.push(this.makeExtrudedQuad(baseStart, baseStart1, actualyHeight, this.transparentMaterial));
            meshes.push(this.makeExtrudedQuad(baseStart1, projectedBaseStart1, actualyHeight, this.opaqueMaterial));

            // 5.5. Close bottom (2 opaque triangles):
            const topA = baseStart1.clone().add(this.isGroundFloor ? new FVector(0, 0, -this.thickness) : new FVector(0));
            const bottomA = baseStart.clone().add(this.isGroundFloor ? new FVector(0, 0, -this.thickness) : new FVector(0));
            const bottomB = projectedBaseStart1.clone().add(this.isGroundFloor ? new FVector(0, 0, -this.thickness) : new FVector(0));

            const bottomMesh = this.makeTriangle(bottomB, bottomA, topA, this.opaqueMaterial);
            const bottomMesh2 = this.makeTriangle(bottomB, bottomA, topA, this.opaqueMaterial);
            const oneSide = this.makeExtrudedQuad(bottomA, topA, this.thickness, this.opaqueMaterial);
            const twoSide = this.makeExtrudedQuad(topA, bottomB, this.thickness, this.opaqueMaterial);
            meshes.push(bottomMesh);

            if (this.isGroundFloor) {
                meshes.push(bottomMesh2, oneSide, twoSide);
            }

            // 5.6 Close top:
            topA.add(new FVector(0, 0, this.isGroundFloor ? this.height : this.height - this.thickness));
            bottomA.add(new FVector(0, 0, this.isGroundFloor ? this.height : this.height - this.thickness));
            bottomB.add(new FVector(0, 0, this.isGroundFloor ? this.height : this.height - this.thickness));

            const topMesh = this.makeTriangle(bottomB, bottomA, topA, this.opaqueMaterial);
            const topMesh2 = this.makeTriangle(bottomB, bottomA, topA, this.opaqueMaterial);
            topMesh2.position.z += this.thickness;
            const oneSide2 = this.makeExtrudedQuad(bottomA, topA, this.thickness, this.opaqueMaterial);
            const twoSide2 = this.makeExtrudedQuad(topA, bottomB, this.thickness, this.opaqueMaterial);
            meshes.push(topMesh, topMesh2, oneSide2, twoSide2);

            // 5.7 Update baseStart for the next panel:
            baseStart = projectedBaseStart1.clone();
        }
        return meshes;
    }

    static getLeadingEdgePanel(facade: THREE.Group): HorizontalEdgePanelInfo | undefined {
        return facade.userData.leadingEdgePanel as HorizontalEdgePanelInfo | undefined;
    }

    static getTrailingEdgePanel(facade: THREE.Group): HorizontalEdgePanelInfo | undefined {
        return facade.userData.trailingEdgePanel as HorizontalEdgePanelInfo | undefined;
    }

    private createEdgePanelsByCorner(panels: Panel[]): Record<string, HorizontalEdgePanelInfo> {
        const edgePanelsByCorner: Record<string, HorizontalEdgePanelInfo> = {};
        const transparentPanels = panels.filter((panel) => panel.mesh.material === this.transparentMaterial);
        if (!transparentPanels.length) return edgePanelsByCorner;

        this.addClosestEdgePanelInfo(edgePanelsByCorner, transparentPanels, this.p1);
        this.addClosestEdgePanelInfo(edgePanelsByCorner, transparentPanels, this.p2);

        return edgePanelsByCorner;
    }

    private addClosestEdgePanelInfo(edgePanelsByCorner: Record<string, HorizontalEdgePanelInfo>, panels: Panel[], corner: FVector) {
        let closestPanel: Panel | undefined;
        let closestDistance = Number.POSITIVE_INFINITY;

        for (const panel of panels) {
            const [a, b] = this.getPanelBottomPoints(panel.mesh);
            const distance = Math.min(HorizontallyOrientedFacade.distanceXY(a, corner), HorizontallyOrientedFacade.distanceXY(b, corner));

            if (distance < closestDistance) {
                closestDistance = distance;
                closestPanel = panel;
            }
        }

        if (!closestPanel || closestDistance > HorizontallyOrientedFacade.EDGE_PANEL_CORNER_THRESHOLD) return;
        this.addEdgePanelInfo(edgePanelsByCorner, closestPanel, corner);
    }

    private addEdgePanelInfo(edgePanelsByCorner: Record<string, HorizontalEdgePanelInfo>, panel: Panel, corner: FVector) {
        const [a, b] = this.getPanelBottomPoints(panel.mesh);
        const aDistance = HorizontallyOrientedFacade.distanceXY(a, corner);
        const bDistance = HorizontallyOrientedFacade.distanceXY(b, corner);
        const outerPoint = aDistance <= bDistance ? a : b;
        const innerPoint = aDistance <= bDistance ? b : a;
        edgePanelsByCorner[HorizontallyOrientedFacade.pointKey(corner)] = {
            ...panel,
            outerPoint,
            innerPoint,
            height: this.getPanelHeight(panel.mesh),
            material: this.transparentMaterial,
        };
    }

    private getPanelBottomPoints(mesh: THREE.Mesh): [FVector, FVector] {
        const positions = mesh.geometry.getAttribute('position');
        return [
            new FVector(positions.getX(1), positions.getY(1), positions.getZ(1)),
            new FVector(positions.getX(3), positions.getY(3), positions.getZ(3)),
        ];
    }

    private getPanelHeight(mesh: THREE.Mesh): number {
        const positions = mesh.geometry.getAttribute('position');
        return Math.abs(positions.getZ(0) - positions.getZ(1));
    }

    // ---------- Geometry helpers ----------
    private projectOnEdge(P: FVector, A: FVector, B: FVector): FVector {
        const AB = B.clone().sub(A);
        const len2 = AB.magSq();
        if (len2 < 1e-12) return A.clone();
        const t = THREE.MathUtils.clamp(P.clone().sub(A).dot(AB) / len2, 0, 1);
        return A.clone().add(AB.multiplyScalar(t));
    }

    private projectPointOnDirection(P: FVector, A: FVector, dir: FVector): FVector {
        const dirNorm = dir.clone().normalize();
        const AP = P.clone().sub(A);
        const projectionLength = AP.dot(dirNorm);
        return A.clone().add(dirNorm.multiplyScalar(projectionLength));
    }

    private projectPointAlongDirectionOntoEdge(P: FVector, A: FVector, B: FVector, dir: FVector): FVector {
        const edgeDir = B.clone().sub(A);
        const denom = edgeDir.x * -dir.y - edgeDir.y * -dir.x;
        if (Math.abs(denom) < 1e-12) return A.clone(); // lines are parallel

        const APx = P.x - A.x;
        const APy = P.y - A.y;
        const u = (APx * -dir.y - APy * -dir.x) / denom;

        // Intersection point
        return A.clone().add(edgeDir.multiplyScalar(u));
    }

    private makeExtrudedQuad(a: FVector, b: FVector, height: number, material: THREE.Material): THREE.Mesh {
        return HorizontallyOrientedFacade.makeVerticalQuad(a, b, height, material);
    }

    private makeTriangle(a: FVector, b: FVector, c: FVector, material: THREE.Material): THREE.Mesh {
        const geom = new THREE.BufferGeometry();
        const vertices = new Float32Array([a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z]);
        geom.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        geom.computeVertexNormals();
        return new THREE.Mesh(geom, material);
    }
}
